import { guymanGame, GUYMAN_SEED_EDIT_TOKEN, GUYMAN_SLUG } from "./guyman-game";
import { makeId, randomToken, slugify } from "./slug";
import type { GameData, SaveGameResponse, StoredGame } from "./types";

const LOCAL_GAMES_KEY = "tribal-trivia-local-games-v1";
const LOCAL_TOKENS_KEY = "tribal-trivia-local-edit-tokens-v1";

interface LocalGameRecord extends StoredGame {
  editToken: string;
}

type LocalGames = Record<string, LocalGameRecord>;

export class ApiError extends Error {
  constructor(message: string, public readonly status = 500) {
    super(message);
  }
}

export async function fetchGame(slug: string): Promise<StoredGame> {
  const apiGame = await request<StoredGame>(`/api/games/${encodeURIComponent(slug)}`, {
    method: "GET"
  });
  if (apiGame) return apiGame;

  const local = getLocalGames()[slug];
  if (local) return stripToken(local);
  if (slug === GUYMAN_SLUG) return guymanGame;
  throw new ApiError("Game not found.", 404);
}

export async function createGame(game: GameData): Promise<SaveGameResponse> {
  const response = await request<SaveGameResponse>("/api/games", {
    method: "POST",
    body: JSON.stringify({ game })
  });
  if (response) return response;

  const stored = saveLocalGame(game, slugify(game.theme || game.title || "trivia-game"));
  return makeSaveResponse(stored, stored.editToken);
}

export async function updateGame(slug: string, token: string, game: GameData): Promise<StoredGame> {
  const response = await request<StoredGame>(`/api/games/${encodeURIComponent(slug)}`, {
    method: "PUT",
    body: JSON.stringify({ token, game })
  });
  if (response) return response;

  const localGames = getLocalGames();
  const existing = localGames[slug];
  if (slug === GUYMAN_SLUG && token === GUYMAN_SEED_EDIT_TOKEN) {
    const updated: LocalGameRecord = {
      ...guymanGame,
      title: game.theme || game.title,
      theme: game.theme,
      game,
      updatedAt: new Date().toISOString(),
      editToken: GUYMAN_SEED_EDIT_TOKEN
    };
    localGames[slug] = updated;
    setLocalGames(localGames);
    rememberEditToken(slug, GUYMAN_SEED_EDIT_TOKEN);
    return stripToken(updated);
  }

  if (!existing || existing.editToken !== token) {
    throw new ApiError("Edit link is invalid for this game.", 403);
  }

  const updated: LocalGameRecord = {
    ...existing,
    title: game.theme || game.title,
    theme: game.theme,
    game,
    updatedAt: new Date().toISOString()
  };
  localGames[slug] = updated;
  setLocalGames(localGames);
  return stripToken(updated);
}

export async function duplicateGame(slug: string): Promise<SaveGameResponse> {
  const response = await request<SaveGameResponse>(`/api/games/${encodeURIComponent(slug)}/duplicate`, {
    method: "POST"
  });
  if (response) return response;

  const source = await fetchGame(slug);
  const copy: GameData = {
    ...structuredClone(source.game),
    theme: `${source.game.theme} Copy`
  };
  const stored = saveLocalGame(copy, slugify(copy.theme));
  return makeSaveResponse(stored, stored.editToken);
}

export function getStoredEditToken(slug: string): string | null {
  if (slug === GUYMAN_SLUG) return GUYMAN_SEED_EDIT_TOKEN;
  const tokens = readJson<Record<string, string>>(LOCAL_TOKENS_KEY, {});
  return tokens[slug] || null;
}

async function request<T>(url: string, init: RequestInit): Promise<T | null> {
  try {
    const response = await fetch(url, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init.headers || {})
      }
    });

    if (response.status === 404 && response.headers.get("x-tribal-trivia-api") !== "1") {
      return null;
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: response.statusText })) as { error?: string };
      throw new ApiError(body.error || "Request failed.", response.status);
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    return null;
  }
}

function saveLocalGame(game: GameData, requestedSlug: string): LocalGameRecord {
  const games = getLocalGames();
  const slug = uniqueSlug(requestedSlug, games);
  const now = new Date().toISOString();
  const editToken = randomToken();
  const stored: LocalGameRecord = {
    id: makeId("game"),
    slug,
    title: game.theme || game.title,
    theme: game.theme,
    visibility: "unlisted",
    game,
    createdAt: now,
    updatedAt: now,
    editToken
  };

  games[slug] = stored;
  setLocalGames(games);
  rememberEditToken(slug, editToken);
  return stored;
}

function makeSaveResponse(game: LocalGameRecord, token: string): SaveGameResponse {
  return {
    game: stripToken(game),
    playUrl: `/play/${game.slug}`,
    editUrl: `/edit/${game.slug}/${token}`,
    token,
    storage: "local"
  };
}

function stripToken(record: LocalGameRecord): StoredGame {
  const { editToken: _editToken, ...game } = record;
  return game;
}

function getLocalGames(): LocalGames {
  return readJson<LocalGames>(LOCAL_GAMES_KEY, {});
}

function setLocalGames(games: LocalGames): void {
  localStorage.setItem(LOCAL_GAMES_KEY, JSON.stringify(games));
}

function rememberEditToken(slug: string, token: string): void {
  const tokens = readJson<Record<string, string>>(LOCAL_TOKENS_KEY, {});
  tokens[slug] = token;
  localStorage.setItem(LOCAL_TOKENS_KEY, JSON.stringify(tokens));
}

function uniqueSlug(baseSlug: string, games: LocalGames): string {
  let slug = baseSlug;
  let suffix = 2;
  while (games[slug] || slug === GUYMAN_SLUG) {
    slug = `${baseSlug}-${suffix}`;
    suffix += 1;
  }
  return slug;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}
